import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  ArrowLeft, User, Loader2, Phone, Mail, MapPin, Briefcase,
  Banknote, PiggyBank, Heart, Calendar, Tag, RefreshCw, Clock,
} from "lucide-react";

/** Per-employee detail page — full KeyPay profile + sub-resources +
 * 12-month timesheet history. */
export default function KeyPayEmployeeDetail() {
  const { clientId, employeeId } = useParams();
  const [data, setData] = useState(null);
  const [ts, setTs] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, t] = await Promise.all([
        api.get(`/integrations/keypay/clients/${clientId}/employees/${employeeId}`),
        api.get(`/integrations/keypay/clients/${clientId}/employees/${employeeId}/timesheets`,
                { params: { limit: 50 } })
          .catch(() => ({ data: null })),
      ]);
      setData(d.data);
      setTs(t.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load employee");
    } finally { setLoading(false); }
  }, [clientId, employeeId]);
  useEffect(() => { load(); }, [load]);

  if (loading || !data) {
    return (
      <div className="p-12 text-center">
        <Loader2 className="h-6 w-6 animate-spin inline" />
      </div>
    );
  }
  const p = data.profile || {};
  const fullName = [p.firstName, p.middleName, p.surname]
    .filter(Boolean).join(" ") || p.name || `Employee ${employeeId}`;
  const isActive = !p.endDate && !p.terminationDate
                   && (p.status || "").toLowerCase() !== "terminated";

  return (
    <div className="p-6 max-w-6xl" data-testid="keypay-employee-detail">
      <Link to={`/integrations/keypay/clients/${clientId}`}
        className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-900 mb-3"
        data-testid="keypay-employee-back">
        <ArrowLeft className="h-3 w-3" /> Back to KeyPay dashboard
      </Link>

      <div className="flex items-center gap-4 mb-6">
        <div className="h-14 w-14 grid place-items-center rounded-sm bg-purple-100 text-purple-700">
          <User className="h-7 w-7" />
        </div>
        <div className="flex-1">
          <div className="label-overline">EMPLOYEE</div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight">
            {fullName}
          </h1>
          <div className="flex items-center gap-2 mt-1">
            <Badge variant="outline"
              className={`rounded-sm text-[9px] ${isActive
                ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                : "bg-zinc-100 text-zinc-600 border-zinc-200"}`}>
              {isActive ? "ACTIVE" : "TERMINATED"}
            </Badge>
            {p.jobTitle && <span className="text-xs text-zinc-600">{p.jobTitle}</span>}
            {p.externalId && <span className="text-[10px] text-zinc-400 font-mono">ext: {p.externalId}</span>}
          </div>
        </div>
        <Button size="sm" variant="outline" className="rounded-sm"
          onClick={load} data-testid="keypay-employee-refresh">
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Block title="Contact" icon={Phone}>
          <Field label="Email" value={p.email || p.emailAddress} icon={Mail} />
          <Field label="Mobile" value={p.mobile || p.mobilePhone} />
          <Field label="Home phone" value={p.homePhone} />
          <Field label="Work phone" value={p.workPhone} />
          <Field label="Address" icon={MapPin}
            value={[p.residentialStreetAddress, p.residentialAddressLine2,
                    p.residentialSuburb, p.residentialState,
                    p.residentialPostCode]
                   .filter(Boolean).join(", ")} />
        </Block>

        <Block title="Employment" icon={Briefcase}>
          <Field label="Status" value={p.status} />
          <Field label="Employment type" value={p.employmentType} />
          <Field label="Job title" value={p.jobTitle} />
          <Field label="Start date" value={fmtDate(p.startDate)} icon={Calendar} />
          <Field label="End date" value={fmtDate(p.endDate || p.terminationDate)} />
          <Field label="Pay schedule" value={p.payScheduleName || p.payScheduleId} />
          <Field label="Primary location" value={p.primaryLocationName || p.locationName} />
          <Field label="Manager" value={p.managerName || p.managerId} />
        </Block>

        <Block title="Pay & rate" icon={Banknote}>
          <Field label="Standard hours / week"
            value={data.standard_hours?.standardHoursPerWeek
                || p.standardHoursPerWeek} />
          <Field label="Pay rate"
            value={p.payRate ? `$${p.payRate}` : null} />
          <Field label="Pay rate unit" value={p.payRateUnit} />
          <Field label="Tax file number"
            value={p.taxFileNumber ? "•••• " + String(p.taxFileNumber).slice(-3) : null} />
          <Field label="Tax category" value={p.taxCategory} />
          <Field label="Annual salary"
            value={p.annualSalary ? `$${Number(p.annualSalary).toLocaleString()}` : null} />
        </Block>

        <Block title="Leave balances" icon={Calendar}>
          {(data.leave_balances || []).length === 0 ? (
            <div className="text-xs italic text-zinc-400">No balances on file.</div>
          ) : (data.leave_balances || []).map((lb, i) => {
            const amt = lb.accruedAmount ?? lb.balance;
            return (
              <Field key={`${lb.leaveCategoryId ?? lb.id ?? lb.leaveCategoryName ?? "lb"}-${i}`}
                label={lb.leaveCategoryName || lb.name || lb.leaveCategory}
                value={amt != null ? `${Number(amt).toFixed(2)} ${lb.unitType || "hrs"}` : "—"} />
            );
          })}
        </Block>

        <Block title="Bank accounts" icon={Banknote}>
          {(data.bank_accounts || []).length === 0 ? (
            <div className="text-xs italic text-zinc-400">No bank accounts on file.</div>
          ) : (data.bank_accounts || []).map((b, i) => (
            <div key={`${b.id ?? b.accountNumber ?? "ba"}-${i}`}
              className="border-b border-zinc-100 last:border-b-0 py-1.5">
              <div className="text-xs font-semibold">{b.accountName}</div>
              <div className="text-[11px] text-zinc-500 font-mono">
                BSB {b.bsb} · Acct ••••{String(b.accountNumber || "").slice(-3)}
              </div>
              {b.allocatedPercentage && (
                <div className="text-[10px] text-zinc-500">{b.allocatedPercentage}% of pay</div>
              )}
            </div>
          ))}
        </Block>

        <Block title="Super funds" icon={PiggyBank}>
          {(data.super_funds || []).length === 0 ? (
            <div className="text-xs italic text-zinc-400">No super funds on file.</div>
          ) : (data.super_funds || []).map((s, i) => (
            <div key={`${s.id ?? s.memberNumber ?? s.usi ?? "sf"}-${i}`}
              className="border-b border-zinc-100 last:border-b-0 py-1.5">
              <div className="text-xs font-semibold">{s.fundName || s.productName}</div>
              <div className="text-[11px] text-zinc-500 font-mono">
                {s.memberNumber && `Member ${s.memberNumber}`}
                {s.usi && ` · USI ${s.usi}`}
              </div>
            </div>
          ))}
        </Block>

        <Block title="Emergency contacts" icon={Heart}>
          {(data.emergency_contacts || []).length === 0 ? (
            <div className="text-xs italic text-zinc-400">No emergency contacts on file.</div>
          ) : (data.emergency_contacts || []).map((c, i) => (
            <div key={`${c.id ?? c.name ?? "ec"}-${i}`}
              className="border-b border-zinc-100 last:border-b-0 py-1.5">
              <div className="text-xs font-semibold">{c.name}</div>
              <div className="text-[11px] text-zinc-500">
                {c.relationship}{c.phone && ` · ${c.phone}`}
              </div>
            </div>
          ))}
        </Block>

        <Block title="Tags" icon={Tag}>
          {(data.tags || []).length === 0 ? (
            <div className="text-xs italic text-zinc-400">No tags assigned.</div>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {(data.tags || []).map((t, i) => (
                <Badge key={`${(typeof t === "object" ? (t.id ?? t.name) : t) || "tag"}-${i}`}
                  className="rounded-sm bg-purple-100 text-purple-800 border-purple-200 text-[10px]">
                  {t.name || t}
                </Badge>
              ))}
            </div>
          )}
        </Block>
      </div>

      {/* ─── Timesheets (last 12 months) ─────────────────────────── */}
      <TimesheetsCard ts={ts} />
    </div>
  );
}

function TimesheetsCard({ ts }) {
  const maxMonthHours = useMemo(() => {
    if (!ts?.hours_by_month) return 0;
    return Math.max(...ts.hours_by_month.map((m) => m.hours), 0);
  }, [ts]);
  if (!ts) return null;
  const hasData = (ts.count || 0) > 0;
  return (
    <Card className="rounded-sm border-zinc-200 mt-4" data-testid="keypay-employee-timesheets">
      <CardContent className="p-0">
        <div className="px-4 py-2 border-b border-zinc-200 bg-zinc-50 text-[10px] uppercase tracking-[0.15em] font-bold text-zinc-600 flex items-center gap-1.5">
          <Clock className="h-3 w-3" /> Timesheets · last 12 months
          <span className="ml-auto font-mono text-zinc-500 normal-case tracking-normal text-[10px]">
            {ts.count} entries · {ts.total_hours?.toFixed(1) || "0.0"} h total
          </span>
        </div>
        {!hasData ? (
          <div className="p-6 text-center text-sm italic text-zinc-500">
            No timesheets recorded in the last 12 months.
          </div>
        ) : (
          <>
            {/* Monthly hours sparkline */}
            <div className="p-4 border-b border-zinc-100">
              <div className="text-[10px] font-mono text-zinc-500 mb-2 uppercase tracking-wider">Hours by month</div>
              <div className="flex items-end gap-1 h-20">
                {(ts.hours_by_month || []).map((m) => (
                  <div key={m.month} className="flex-1 flex flex-col items-center gap-1 group min-w-0">
                    <div className="text-[9px] font-mono text-zinc-400 opacity-0 group-hover:opacity-100 transition">
                      {m.hours.toFixed(0)}h
                    </div>
                    <div
                      className="w-full bg-[#FF6B35] rounded-sm transition-all hover:bg-[#FF6B35]/80"
                      style={{ height: `${maxMonthHours ? Math.max(2, (m.hours / maxMonthHours) * 60) : 2}px` }}
                      title={`${m.month}: ${m.hours.toFixed(1)}h`} />
                    <div className="text-[8px] font-mono text-zinc-500 truncate w-full text-center">
                      {m.month.slice(5)}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Status breakdown */}
            {ts.status_counts && Object.keys(ts.status_counts).length > 0 && (
              <div className="px-4 py-2 border-b border-zinc-100 flex items-center gap-2 flex-wrap">
                {Object.entries(ts.status_counts).map(([s, n]) => (
                  <Badge key={s} variant="outline" className={`rounded-sm text-[10px] ${
                    s === "approved" || s === "processed" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : s === "submitted" ? "bg-amber-50 text-amber-700 border-amber-200"
                    : s === "rejected" ? "bg-red-50 text-red-700 border-red-200"
                    : "bg-zinc-50 text-zinc-700 border-zinc-200"
                  }`}>
                    {s}: {n}
                  </Badge>
                ))}
              </div>
            )}

            {/* Recent entries */}
            <div className="grid grid-cols-[100px_1fr_1fr_70px_90px] gap-2 px-4 py-2 bg-zinc-50/50 border-b border-zinc-100 text-[10px] uppercase tracking-[0.15em] font-bold text-zinc-600">
              <span>Date</span>
              <span>Work type</span>
              <span>Location</span>
              <span className="text-right">Hours</span>
              <span className="text-right">Status</span>
            </div>
            <div className="divide-y divide-zinc-100 max-h-[420px] overflow-y-auto">
              {(ts.entries || []).map((e) => (
                <div key={e.id}
                  className="grid grid-cols-[100px_1fr_1fr_70px_90px] gap-2 px-4 py-1.5 text-xs items-center hover:bg-zinc-50">
                  <span className="font-mono text-zinc-600">{e.date}</span>
                  <span className="truncate">{e.work_type || "—"}</span>
                  <span className="truncate text-zinc-500">{e.location || "—"}</span>
                  <span className="font-mono text-right">{e.hours.toFixed(2)}</span>
                  <span className="text-right">
                    <Badge variant="outline" className={`rounded-sm text-[9px] ${
                      e.status === "approved" || e.status === "processed" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                      : e.status === "submitted" ? "bg-amber-50 text-amber-700 border-amber-200"
                      : e.status === "rejected" ? "bg-red-50 text-red-700 border-red-200"
                      : "bg-zinc-50 text-zinc-700 border-zinc-200"
                    }`}>
                      {e.status}
                    </Badge>
                  </span>
                </div>
              ))}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function Block({ title, icon: Icon, children }) {
  return (
    <Card className="rounded-sm border-zinc-200">
      <CardContent className="p-0">
        <div className="px-3 py-2 border-b border-zinc-200 bg-zinc-50 text-[10px] uppercase tracking-[0.15em] font-bold text-zinc-600 flex items-center gap-1.5">
          {Icon && <Icon className="h-3 w-3" />} {title}
        </div>
        <div className="p-3 space-y-1.5">{children}</div>
      </CardContent>
    </Card>
  );
}

function Field({ label, value, icon: Icon }) {
  if (value == null || value === "" || value === false) return null;
  return (
    <div className="flex items-start gap-2 py-0.5">
      {Icon && <Icon className="h-3 w-3 text-zinc-400 mt-0.5" />}
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-[0.12em] text-zinc-500 font-bold">{label}</div>
        <div className="text-xs font-mono break-words">{String(value)}</div>
      </div>
    </div>
  );
}

function fmtDate(s) {
  if (!s) return null;
  return String(s).slice(0, 10);
}
